type Index = { i: number; j: number };

export class Matrix<T> {
    private readonly size: [number, number]; // [rows columns]
    private values: ({ value?: T } & Index)[][];

    constructor(n: number, m: number) {
        this.size = [n, m];
        this.values = Array.from({ length: n }).map((_, i) => Array.from({ length: m }).map((_, j) => ({ i, j })));
    }

    setValue(i: number, j: number, value: T) {
        this.values[i][j].value = value;
    }

    getValue(i: number, j: number) {
        return this.values[i][j].value;
    }

    insertRowAndFill(position: number, fillValue: T) {
        const row = Array.from({ length: this.size[1] }).map((_, j) => ({
            i: position,
            j,
            value: fillValue,
        }));

        this.values.splice(position, 0, row);
        this.size[0]++;
    }

    insertColumnAndFill(position: number, fillValue: T) {
        this.values.forEach((row, i) => {
            row.splice(position, 0, { i, j: position, value: fillValue });
        });
        this.size[1]++;
    }

    map<K>(fn: (value: T | undefined, i: number, j: number) => K) {
        const mappedMatrix = new Matrix<K>(this.size[0], this.size[1]);

        this.forEach((value, i, j) => {
            const newValue = fn(value, i, j);
            mappedMatrix.setValue(i, j, newValue);
        });

        return mappedMatrix;
    }

    forEach(fn: (value: T | undefined, i: number, j: number) => void) {
        this.values.forEach((row, i) => {
            row.forEach((el, j) => {
                fn(el.value, i, j);
            });
        });
    }

    forEachRow(fn: (row: (T | undefined)[], i: number) => void) {
        this.values.forEach((row, i) => {
            fn(
                row.map(({ value }) => value),
                i,
            );
        });
    }

    forEachColumn(fn: (column: (T | undefined)[], j: number) => void) {
        for (let j = 0; j < this.size[1]; j++) {
            const column = this.values.map((row) => row[j].value);
            fn(column, j);
        }
    }

    findAll(predicate: (value: T | undefined, i: number, j: number) => boolean) {
        const found: ({ value?: T } & Index)[] = [];
        this.values.forEach((row, i) => {
            row.forEach((el, j) => {
                if (predicate(el.value, i, j)) {
                    found.push({ value: el.value, i, j });
                }
            });
        });

        return found;
    }

    getValues() {
        return this.values.map((row) => {
            return row.map((el) => el.value);
        });
    }

    toArray() {
        const array: (T | undefined)[] = [];
        this.forEach((value) => {
            array.push(value);
        });
        return array;
    }
}
